<template>
    <div v-if="segment" class="c-view c-view-sticky-footer c-entities">
        <InternalHeader>
            <div slot="title" class="c-internal-header__title" v-if="segment">
                {{ segment.name }}
                <tag
                    v-if="segment.tag_enabled"
                    :tag="segment.tag_name"
                    :color="segment.tag_color ? segment.tag_color : undefined"
                    class="ml-2"
                ></tag>
                <segment-save
                    slot="save-segment"
                    :segment="segment"
                    @save="updateSegment"
                >
                    <a
                        slot="trigger"
                        class="text-primary"
                        style="font-size: 13px"
                        >Edit</a
                    >
                </segment-save>
            </div>
        </InternalHeader>

        <div class="c-view-sticky-footer__content">
            <Alert
                v-if="segment.version == 1"
                type="warning"
                class="text-center"
            >
                This segment was created in the previous version of Akita and
                can't be edited here. To edit this segment, please switch to the
                previous version.
            </Alert>
            <div v-else>
                <Row class="mb-4 util-min-h40">
                    <Col span="24">
                        <div v-if="filtersReady">
                            <entity-filters-edit
                                :filter-group="filterGroup"
                                :available-filters="availableFilters"
                                @updated="onEntityFiltersUpdated"
                            >
                                <ownership-toggle
                                    slot="owner"
                                    v-bind:value.sync="owner"
                                    class="mr-2"
                                ></ownership-toggle>

                                <Button
                                    slot="save-segment"
                                    v-if="saveEnabled"
                                    @click="updateFilterGroup"
                                    custom-icon="icon icon-floppy-disk"
                                    class="text-primary ml-2 text-danger"
                                >
                                    Save Changes
                                </Button>
                            </entity-filters-edit>
                        </div>
                    </Col>
                </Row>

                <Row class="mb-4">
                    <Col span="24">
                        <div v-if="statisticsReady">
                            <segment-statistics
                                :pinnable="true"
                                :read-only="false"
                                :segment="segment"
                                :filter-group="filterGroup"
                                :modifiers="modifiers"
                                :selected-statistics="selectedStatistics"
                                :available-statistics="availableStatistics"
                            >
                            </segment-statistics>
                        </div>
                    </Col>
                </Row>

                <Row class="c-entities__table-container">
                    <Col span="24">
                        <Row type="flex" justify="space-between" class="mb-4">
                            <div v-if="columnsReady">
                                <column-select
                                    :selected-columns="selectedColumns"
                                    :available-columns="availableColumns"
                                >
                                    <Button
                                        slot="trigger"
                                        href="javascript:void(0"
                                        class="text-primary"
                                    >
                                        {{
                                            $t(
                                                "entityColumns.labels.buttonText",
                                                {
                                                    count: selectedColumns.length,
                                                }
                                            )
                                        }}
                                    </Button>
                                </column-select>
                            </div>
                        </Row>

                        <Row class="mb-4">
                            <Col span="24">
                                <contacts-table
                                    @sort="onSort"
                                    :loading="loading"
                                    :rows="dataRows"
                                    :columns="dataColumns"
                                    :sort-by="sortBy"
                                    :border="true"
                                >
                                </contacts-table>
                            </Col>
                        </Row>

                        <Footer>
                            <Pagination
                                :current="pageCurrent"
                                :total="pageTotal"
                                :page-size="pageSize"
                                @page-change="onPageChange"
                                @page-size-change="onPageSizeChange"
                                show-sizer
                                :page-size-opts="[10, 25, 50, 100]"
                            />
                        </Footer>
                    </Col>
                </Row>
            </div>
        </div>
    </div>
</template>

<script>
import FilterGroup from "@/js/components/partials/filters/models/filter-group";
import AvailableColumns from "@/js/lookups/contact-columns";
import AvailableFilters from "@/js/lookups/contact-filters.js";
import AvailableStatistics from "@/js/lookups/contact-statistics.js";
import Statistics from "@/js/mixins/statistics.js";

export default {
    mixins: [Statistics],
    props: {
        segmentId: Number,
        contactSearchRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/contact-search.js"))();
            },
        },
        segmentsRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/segments.js"))();
            },
        },
    },
    data: function () {
        return {
            segment: null,
            unwatchSegment: null,
            loading: false,
            availableColumns: null,
            availableFilters: null,
            availableStatistics: null,
            localStorageKeySortBy: "segments_" + this.segmentId + "_sortBy",
            localStorageKeyOwner: "contacts_owner",
            localStorageKeyPageSize: "contacts_pageSize",
            localStorageKeyStatistics:
                "segments_" + this.segmentId + "_statistics",
            filterGroup: null,
            selectedColumns: [],
            selectedStatistics: null,
            sortBy: null,
            owner: "all",
            data: null,
            pageCurrent: 1,
            pageSize: null,
        };
    },
    watch: {
        segmentId() {
            this.load();
        },
        segment: {
            handler() {
                if (this.segment) {
                    this.$root.$emit(
                        "page-title:update",
                        "Edit " + this.segment.name
                    );
                }
            },
            deep: true,
        },
    },
    components: {
        ContactsTable: function () {
            return import("@/js/components/partials/contacts-table");
        },
        ColumnSelect: function () {
            return import("@/js/components/partials/column-select");
        },
        EntityFiltersEdit: function () {
            return import("@/js/components/partials/filters/edit.vue");
        },
        SegmentStatistics: function () {
            return import("@/js/components/partials/segment-statistics");
        },
        InternalHeader: function () {
            return import("@/js/components/partials/header");
        },
        OwnershipToggle: function () {
            return import("@/js/components/partials/accounts-ownership-toggle");
        },
        Pagination: function () {
            return import("@/js/components/partials/pagination");
        },
        Tag: function () {
            return import("@/js/components/partials/tag");
        },
        SegmentSave: function () {
            return import("@/js/components/partials/segment-save");
        },
    },
    created() {
        this.saveStatistics = _.debounce(this.saveStatistics, 2000, {
            leading: false,
            trailing: true,
        });
        this.query = _.debounce(this.query, 1000, {
            leading: false,
            trailing: true,
        });
        this.load();
    },
    computed: {
        statisticsReady() {
            return (
                this.selectedStatistics &&
                true === _.get(this.availableStatistics, "initialized", false)
            );
        },
        columnsReady() {
            return (
                this.selectedColumns &&
                true === _.get(this.availableColumns, "initialized", false)
            );
        },
        dataColumns() {
            let columnData = _.get(this.data, "columns", []);
            return _.map(columnData, function (column) {
                return Object.assign(column, {
                    title: column.label,
                });
            });
        },
        dataRows() {
            return _.get(this.data, "data", []);
        },
        filtersReady() {
            return (
                this.filterGroup &&
                true === _.get(this.availableFilters, "initialized", false)
            );
        },
        modifiers() {
            return [this.owner];
        },
        pageTotal() {
            return _.get(this.data, "meta.total", 0);
        },
        saveEnabled() {
            if (this.filterGroup.editing()) {
                return false;
            }

            if (!this.filterGroup.valid()) {
                return false;
            }

            var definition = JSON.parse(
                _.get(this.segment, "definition", "{}")
            );
            let original = new FilterGroup(definition.filters);

            if (_.isEqual(original.hash(), this.filterGroup.hash())) {
                return false;
            }

            return true;
        },
    },
    methods: {
        initializeFilters() {
            var definition = JSON.parse(
                _.get(this.segment, "definition", "{}")
            );
            this.filterGroup = new FilterGroup(
                definition.filters,
                this.availableFilters
            );
            this.$watch("filterGroup", this.onEntityFiltersUpdated, {
                deep: true,
            });
        },
        initializeColumns() {
            var definition = JSON.parse(
                _.get(this.segment, "definition", "{}")
            );
            this.selectedColumns = _.get(definition, "columns", []);
            this.$watch("selectedColumns", this.onColumnsChange, {
                deep: true,
            });
        },
        initializeSortBy() {
            var sortBy = this.$localStorageCache.get(
                this.localStorageKeySortBy
            );
            var definition = JSON.parse(
                _.get(this.segment, "definition", "{}")
            );
            this.sortBy =
                JSON.parse(sortBy) ||
                _.get(definition, "sortBy", {
                    column: "pers__full_name",
                    order: "asc",
                    dataTypeId: "1",
                });
            this.$watch("sortBy", this.onSortByChange, { deep: true });
        },
        initializeOwner() {
            var owner = this.$localStorageCache.get(this.localStorageKeyOwner);
            this.owner = owner || "all"; // mine, unassigned
            this.$watch("owner", this.onOwnerChange, { deep: true });
        },
        initializePageSize() {
            var pageSize = this.$localStorageCache.get(
                this.localStorageKeyPageSize
            );
            this.pageSize = pageSize || 25;
        },
        async initAvailableStatistics() {
            var cacheKey = "contacts_available_statistics";
            var cached = this.$localStorageCache.get(cacheKey);
            if (cached) {
                this.availableStatistics = JSON.parse(cached);
                return;
            }
            this.availableStatistics = new AvailableStatistics();
            await this.availableStatistics.init(this.$i18n, this.$store);
            this.$localStorageCache.set(
                cacheKey,
                JSON.stringify(this.availableStatistics),
                60 * 60 * 24
            );
        },
        async initAvailableColumns() {
            var cacheKey = "contacts_available_columns";
            var cached = this.$localStorageCache.get(cacheKey);
            if (cached) {
                this.availableColumns = JSON.parse(cached);
                return;
            }
            this.availableColumns = new AvailableColumns();
            await this.availableColumns.init(this.$i18n, this.$store);
            this.$localStorageCache.set(
                cacheKey,
                JSON.stringify(this.availableColumns),
                60 * 60 * 24
            );
        },
        async initAvailableFilters() {
            var cacheKey = "contacts_available_filters";
            var cached = this.$localStorageCache.get(cacheKey);
            if (cached) {
                this.availableFilters = JSON.parse(cached);
                return;
            }
            this.availableFilters = new AvailableFilters();
            await this.availableFilters.init(this.$i18n, this.$store);
            this.$localStorageCache.set(
                cacheKey,
                JSON.stringify(this.availableFilters),
                60 * 60 * 24
            );
            return;
        },
        async initializeStatistics() {
            let request = {};
            const response = await this.segmentsRepository
                .statistics(this.segmentId)
                .index(request);
            // So we can access teh search function
            const $availableStatistics = new AvailableStatistics();
            // this may be serialized so can't access functions

            this.selectedStatistics = [];

            for (var i = response.data.data.length - 1; i >= 0; i--) {
                let statistic = response.data.data[i];
                let item;
                switch (statistic.type_id) {
                    case 5: // COUNT
                        item = $availableStatistics.lookupByKey(
                            this.availableStatistics.children,
                            "SegmentCount"
                        );
                        if (item) {
                            item = _.get(item, "definition");
                        }
                        break;
                    case 6: // NPS
                        item = $availableStatistics.lookupByKey(
                            this.availableStatistics.children,
                            "SegmentNps"
                        );
                        if (item) {
                            item = _.get(item, "definition");
                        }
                        break;
                    case 7: // PRIMARY SCORE
                        item = $availableStatistics.lookupByKey(
                            this.availableStatistics.children,
                            "SegmentScore"
                        );
                        if (item) {
                            item = _.get(item, "definition");
                        }
                        if (item) {
                            item = _.merge(_.get(item, "definition"), {
                                aggregate: statistic.aggregate,
                            });
                        }
                        break;
                    case 2: // METRIC
                        item = $availableStatistics.lookupByKey(
                            this.availableStatistics.children,
                            "SegmentMetric__" + statistic.related_id
                        );
                        if (item) {
                            item = _.merge(_.get(item, "definition"), {
                                aggregate: statistic.aggregate,
                            });
                        }
                        break;
                    case 3: // HEALTH SCORE SET
                        item = $availableStatistics.lookupByKey(
                            this.availableStatistics.children,
                            "SegmentScoreSet__" + statistic.related_id
                        );
                        if (item) {
                            item = _.merge(_.get(item, "definition"), {
                                aggregate: statistic.aggregate,
                            });
                        }
                        break;
                    case 4: // TRAIT
                        item = $availableStatistics.lookupByKey(
                            this.availableStatistics.children,
                            "SegmentTrait__" + statistic.related_id
                        );
                        if (item) {
                            item = _.merge(_.get(item, "definition"), {
                                aggregate: statistic.aggregate,
                            });
                        }
                        break;
                }

                if (item) {
                    item.segmentId = this.segmentId;
                    this.selectedStatistics.push(_.cloneDeep(item));
                }
            }

            this.$watch("selectedStatistics", this.onSelectedStatisticsChange, {
                deep: true,
            });
        },
        async load() {
            await this.loadSegment();
            if (this.segment.version == 1) {
                return;
            }
            this.initializeColumns();
            this.initializeSortBy();
            this.initializeOwner();
            this.initializePageSize();
            this.initAvailableColumns();

            await this.initAvailableFilters();
            this.initializeFilters();
            this.query();

            await this.initAvailableStatistics();
            this.initializeStatistics();
        },
        async loadSegment() {
            this.loading = true;
            if (this.unwatchSegment) {
                this.unwatchSegment();
                this.unwatchSegment = null;
            }
            const response = await this.segmentsRepository.show(this.segmentId);
            this.loading = false;
            this.segment = response.data.data;
            this.unwatchSegment = this.$watch("segment", this.saveSegment, {
                deep: true,
            });
        },
        async query() {
            this.loading = true;
            let request = {
                q: JSON.stringify(this.filterGroup.compress()),
                columns: JSON.stringify(this.selectedColumns),
                modifiers: "owner" === this.owner ? ["owner"] : [],
                sort_by:
                    this.sortBy.column && this.sortBy.order
                        ? this.sortBy.column + ":" + this.sortBy.order
                        : null,
                page: this.pageCurrent,
                per_page: this.pageSize,
            };
            let response;
            try {
                response = await this.contactSearchRepository.index(request);
            } catch (e) {
                this.loading = false;
                this.data = [];
                this.pageCurrent = 1;
                // In case the error was caused by sorting
                _.merge(this.sortBy, {
                    column: null,
                    order: null,
                    dataTypeId: null,
                });
                return;
            }
            this.loading = false;
            this.data = response.data;
            this.pageCurrent = _.get(response.data, "meta.current_page", 1);
            this.pageSize = _.get(response.data, "meta.per_page", 25);
        },
        onPageChange(page) {
            this.pageCurrent = page;
            this.query();
        },
        onPageSizeChange(size) {
            this.$localStorageCache.set(this.localStorageKeyPageSize, size);
            this.pageCurrent = 1;
            this.pageSize = size;
            this.query();
        },
        onSort(definition) {
            this.pageCurrent = 1;
            _.merge(this.sortBy, definition);
        },
        onEntityFiltersUpdated() {
            if (!this.filterGroup.valid()) {
                return false;
            }
            if (this.filterGroup.editing()) {
                return false;
            }
            this.$localStorageCache.set(
                this.localStorageKeyFilters,
                JSON.stringify(this.filterGroup)
            );
            this.query();
        },
        onColumnsChange() {
            this.$localStorageCache.set(
                this.localStorageKeyColumns,
                JSON.stringify(this.selectedColumns)
            );
            var definition = JSON.parse(this.segment.definition);
            definition.columns = this.selectedColumns;
            this.segment.definition = JSON.stringify(definition);
            this.query();
        },
        onSortByChange() {
            this.$localStorageCache.set(
                this.localStorageKeySortBy,
                JSON.stringify(this.sortBy)
            );
            var definition = JSON.parse(this.segment.definition);
            definition.sort_by = this.sortBy;
            this.segment.definition = JSON.stringify(definition);
            this.query();
        },
        onSelectedStatisticsChange() {
            this.saveStatistics(this.segmentId);
        },
        onOwnerChange() {
            this.$localStorageCache.set(this.localStorageKeyOwner, this.owner);
            this.query();
        },
        updateSegment(data) {
            this.segment = _.merge(this.segment, data);
        },
        updateFilterGroup() {
            var definition = JSON.parse(this.segment.definition);
            definition.filters = this.filterGroup;
            this.segment.definition = JSON.stringify(definition);
        },
        async saveSegment() {
            const response = await this.segmentsRepository.update(
                this.segmentId,
                this.segment
            );
            this.$root.$emit("segment:update", this.segment);
        },
    },
};
</script>
